import { createFileRoute, Link, Navigate, Outlet } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";

import { useMe } from "@/lib/hooks";

export const Route = createFileRoute("/_authed/admin")({
  component: AdminLayout,
});

const subTabBase = "-mb-px border-b-2 px-1 pb-2 text-sm transition-colors";
const subTabActive = { className: "border-white text-white font-medium" };
const subTabInactive = {
  className: "border-transparent text-[var(--muted-foreground)] hover:text-white",
};

/**
 * AdminLayout is the chrome for the admin section: a client-side admin guard plus
 * the Overview / Users / Audit sub-navigation around the page outlet. The guard
 * is a UX redirect only — the real boundary is the server's RequireAdmin on every
 * /admin/* data endpoint, so a forged client still gets 403s and no data.
 */
function AdminLayout() {
  const me = useMe();

  // Admin status still resolving — spinner rather than a premature redirect.
  if (me.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="size-5 animate-spin text-[var(--muted-foreground)]" />
      </div>
    );
  }

  // Non-admin (or /auth/me unavailable) — send them to the authenticated home.
  if (!me.data?.is_admin) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <div className="space-y-5">
      <nav className="flex gap-5 border-b border-[var(--border)]">
        <Link
          to="/admin"
          activeOptions={{ exact: true }}
          className={subTabBase}
          activeProps={subTabActive}
          inactiveProps={subTabInactive}
        >
          Overview
        </Link>
        <Link
          to="/admin/users"
          className={subTabBase}
          activeProps={subTabActive}
          inactiveProps={subTabInactive}
        >
          Users
        </Link>
        <Link
          to="/admin/audit"
          className={subTabBase}
          activeProps={subTabActive}
          inactiveProps={subTabInactive}
        >
          Audit log
        </Link>
      </nav>
      <Outlet />
    </div>
  );
}
